// PRD.314 — Visit Note Specialty History section.
//
// ONE generic component serving every specialty type. Dental and Derma differ
// only by the `type` prop — there is no Dental-specific or Derma-specific
// persistence, query or editor logic anywhere.
//
// Storage is the generic `encounter_specialty_histories` relation, one logical
// row per (encounter_id, type). Medical History is NOT routed through here; it
// keeps `patient_encounters.medical_history` (PRD.310).
//
// Presentation reuses PRD.313's `LongitudinalHistoryEditor` and PRD.314's
// extracted `HistoryEntryEditor`, so the surface, the autosave lifecycle and
// the voice pipeline are literally the same code Medical History runs.
//
// Specialty histories have NO patient-level baseline in this PRD (§12), so
// `patientRecord` is always null — no fake "Patient Record" source is rendered.

import { useCallback, useMemo } from 'react';
import {
  SPECIALTY_HISTORY_LABELS,
  getSpecialtyHistorySettings,
  qAppConfig,
  qSpecialtyHistoriesByPatient,
  selectCurrentSpecialtyHistory,
  selectPriorSpecialtyHistory,
  specialtyHistoryId,
  type AppConfigRow,
  type EncounterSpecialtyHistoryRow,
  type PatientEncounterRow,
  type SpecialtyHistoryType,
} from '@helios/data-access';
import { useAuth } from '@helios/auth-firebase-web';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { HistoryEntryEditor } from './HistoryEntryEditor';
import { LongitudinalHistoryEditor } from './LongitudinalHistoryEditor';
import { toLongitudinalEntries } from './longitudinal-history-logic';
import {
  formatVisitDate,
  useEncounterChronology,
  useVisitDateByEncounter,
} from './patient-image-visits';

export function SpecialtyHistorySection({
  encounter,
  patientId,
  type,
}: {
  encounter: PatientEncounterRow;
  patientId: string;
  /** Stable clinical identity — 'dental' | 'derma'. Never the UI label. */
  type: SpecialtyHistoryType;
}) {
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const label = SPECIALTY_HISTORY_LABELS[type];

  // PRD.314 §14 — Specialty History has its OWN clinic capability group
  // (`app_config.extra_json.specialty_history`), so the Medical History
  // switches keep meaning exactly what they say. Both default ON.
  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const settings = useMemo(
    () => getSpecialtyHistorySettings(appConfigRows[0] ?? null),
    [appConfigRows],
  );

  // Scoped to this type, so dental rows can never surface under derma.
  const { data: rows = [] } = usePowerSyncQuery<EncounterSpecialtyHistoryRow>(
    qSpecialtyHistoriesByPatient(patientId, type),
  );

  const visitDateByEncounter = useVisitDateByEncounter(
    settings.showPreviousHistory ? patientId : '',
  );
  const chronologyByEncounter = useEncounterChronology(
    settings.showPreviousHistory ? patientId : '',
  );

  const priorEntries = useMemo(
    () =>
      settings.showPreviousHistory
        ? selectPriorSpecialtyHistory(
            rows,
            encounter.id,
            visitDateByEncounter,
            chronologyByEncounter,
          )
        : [],
    [settings.showPreviousHistory, rows, encounter.id, visitDateByEncounter, chronologyByEncounter],
  );

  const entries = useMemo(
    () => toLongitudinalEntries(priorEntries, formatVisitDate),
    [priorEntries],
  );

  const storedValue = useMemo(
    () => selectCurrentSpecialtyHistory(rows, encounter.id),
    [rows, encounter.id],
  );

  /**
   * Upsert the one logical row for this encounter + type.
   *
   * `identity` is `<encounterId>:<type>` — the deterministic row id — so an
   * in-flight save always lands on the record the text was typed into, even if
   * the clinician has since switched encounter or specialty.
   *
   * PRD §10 — no row is created merely by rendering: the first write happens
   * only when there is something to store, and clearing an existing entry
   * updates it to empty rather than inserting a blank row.
   */
  const saveCurrent = useCallback(
    async (text: string, identity: string) => {
      if (!db) return;
      const now = new Date().toISOString();

      // PRD §10 — nothing meaningful to store means no row is created. A blind
      // UPDATE is exactly right here: it clears an existing entry and is a
      // no-op (no CRUD entry, no INSERT) when the row was never created, so
      // simply rendering the section never writes.
      if (!text.trim()) {
        await db.execute(
          'UPDATE encounter_specialty_histories SET text = ?, updated_at = ? WHERE id = ?',
          [null, now, identity],
        );
        return;
      }

      // INSERT-first, UPDATE on the PK collision — the repo's PowerSync upsert
      // convention (see `useUserUiPreference`). PowerSync client tables are
      // id-keyed VIEWS over ps_data, so `ON CONFLICT` raises "cannot UPSERT a
      // view", and a check-then-insert would race two overlapping flushes into
      // a duplicate INSERT.
      try {
        await db.execute(
          `INSERT INTO encounter_specialty_histories
             (id, org_id, clinic_id, patient_id, encounter_id, type, text, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [identity, orgId, null, patientId, encounter.id, type, text, now, now],
        );
      } catch {
        await db.execute(
          'UPDATE encounter_specialty_histories SET text = ?, updated_at = ? WHERE id = ?',
          [text, now, identity],
        );
      }
    },
    [db, orgId, patientId, encounter.id, type],
  );

  return (
    <div
      style={{
        padding: 'var(--sp-4) var(--sp-5)',
        // Claim the column's full height (a no-op outside the grid) so the
        // editor below can grow into whatever space the row has.
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <LongitudinalHistoryEditor
        label={label}
        // §12 — no patient-scoped specialty history exists yet.
        patientRecord={null}
        previousEntries={entries}
        showHistory={settings.showPreviousHistory}
      >
        <HistoryEntryEditor
          identity={specialtyHistoryId(encounter.id, type)}
          storedValue={storedValue}
          onSave={saveCurrent}
          label={label}
          placeholder={`Add to ${label.toLowerCase()}...`}
          voiceEnabled={settings.voiceToTextEnabled}
        />
      </LongitudinalHistoryEditor>
    </div>
  );
}
